// Your X profile from your X archive, read in the browser only (BANGERS.md slice 7). When someone asks for it, after
// the import, the zip they already picked is opened here: just data/account.js, data/profile.js and the one picture
// in data/profile_media/. Nothing else is inflated, and nothing leaves the browser until they press Save (bio and a
// link to their X go to social's profile card; the picture becomes their portrait in accounts). The server-side
// import keeps its promise: it reads only who you follow and who follows you.

export interface XProfile {
  username: string | null;
  displayName: string | null;
  bio: string | null;
  picture: Blob | null;
}

interface Entry {
  name: string;
  method: number;
  compSize: number;
  offset: number;
}

const u16 = (v: DataView, o: number) => v.getUint16(o, true);
const u32 = (v: DataView, o: number) => v.getUint32(o, true);
const u64 = (v: DataView, o: number) => Number(v.getBigUint64(o, true));
const bytes = async (f: Blob, start: number, end: number) => new DataView(await f.slice(start, end).arrayBuffer());

/** The zip's central directory: only names, sizes and offsets (Zip64 included; X archives can pass 4 GB). */
export async function zipEntries(f: Blob): Promise<Entry[]> {
  const tailStart = Math.max(0, f.size - 65_557);
  const tail = await bytes(f, tailStart, f.size);
  let eocd = -1;
  for (let i = tail.byteLength - 22; i >= 0; i--) if (u32(tail, i) === 0x06054b50) { eocd = i; break; }
  if (eocd < 0) throw new Error("That file isn't a zip.");
  let count = u16(tail, eocd + 10);
  let size = u32(tail, eocd + 12);
  let start = u32(tail, eocd + 16);
  if ((start === 0xffffffff || count === 0xffff || size === 0xffffffff) && eocd >= 20 && u32(tail, eocd - 20) === 0x07064b50) {
    const z = await bytes(f, u64(tail, eocd - 20 + 8), u64(tail, eocd - 20 + 8) + 56);
    if (u32(z, 0) !== 0x06064b50) throw new Error("That zip's directory is damaged.");
    count = u64(z, 32);
    size = u64(z, 40);
    start = u64(z, 48);
  }
  if (size > 64 * 1024 * 1024) throw new Error("That zip's directory is too large to read here.");
  const cd = await bytes(f, start, start + size);
  const dec = new TextDecoder();
  const out: Entry[] = [];
  for (let p = 0, n = 0; n < count && p + 46 <= cd.byteLength; n++) {
    if (u32(cd, p) !== 0x02014b50) break;
    const nameLen = u16(cd, p + 28);
    const extraLen = u16(cd, p + 30);
    const commentLen = u16(cd, p + 32);
    let compSize = u32(cd, p + 20);
    let usize = u32(cd, p + 24);
    let offset = u32(cd, p + 42);
    const name = dec.decode(new Uint8Array(cd.buffer, cd.byteOffset + p + 46, nameLen));
    // Zip64 extra field: the 0xffffffff values follow in order (uncompressed, compressed, offset)
    for (let e = p + 46 + nameLen; e + 4 <= p + 46 + nameLen + extraLen; ) {
      const id = u16(cd, e);
      const len = u16(cd, e + 2);
      if (id === 1) {
        let q = e + 4;
        if (usize === 0xffffffff) { usize = u64(cd, q); q += 8; }
        if (compSize === 0xffffffff) { compSize = u64(cd, q); q += 8; }
        if (offset === 0xffffffff) offset = u64(cd, q);
      }
      e += 4 + len;
    }
    out.push({ name, method: u16(cd, p + 10), compSize, offset });
    p += 46 + nameLen + extraLen + commentLen;
  }
  return out;
}

/** One entry's bytes (stored or deflated), capped at `max`. */
export async function readEntry(f: Blob, e: Entry, max = 8 * 1024 * 1024): Promise<Uint8Array> {
  if (e.compSize > max) throw new Error(`${e.name} is too large.`);
  const head = await bytes(f, e.offset, e.offset + 30);
  if (u32(head, 0) !== 0x04034b50) throw new Error("That zip is damaged.");
  const at = e.offset + 30 + u16(head, 26) + u16(head, 28);
  const raw = f.slice(at, at + e.compSize);
  if (e.method === 0) return new Uint8Array(await raw.arrayBuffer());
  if (e.method !== 8) throw new Error(`${e.name} uses a compression this page can't read.`);
  const out = await new Response(raw.stream().pipeThrough(new DecompressionStream("deflate-raw"))).arrayBuffer();
  if (out.byteLength > max) throw new Error(`${e.name} is too large.`);
  return new Uint8Array(out);
}

/** `window.YTD.<name>.part0 = [...]` → the array, without evaluating anything. */
export function ytd(text: string): unknown[] {
  const eq = text.indexOf("=");
  if (eq < 0 || !/^\s*window\.YTD\.[A-Za-z0-9_]+\.part\d+\s*$/.test(text.slice(0, eq).replace(/^﻿/, ""))) return [];
  try {
    const v = JSON.parse(text.slice(eq + 1).trim().replace(/;$/, ""));
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}

/** The profile fields from account.js and profile.js (already parsed). */
export function profileFrom(account: unknown[], profile: unknown[]): Omit<XProfile, "picture"> {
  const a = ((account[0] as Record<string, unknown> | undefined)?.account ?? {}) as Record<string, unknown>;
  const p = ((profile[0] as Record<string, unknown> | undefined)?.profile ?? {}) as Record<string, unknown>;
  const d = (p.description ?? {}) as Record<string, unknown>;
  const username = typeof a.username === "string" && /^[A-Za-z0-9_]{1,15}$/.test(a.username) ? a.username : null;
  const displayName = typeof a.accountDisplayName === "string" ? a.accountDisplayName.trim().slice(0, 40) || null : null;
  // X shortens links in bios to t.co; those are dropped rather than shown as someone else's redirect
  const bio = typeof d.bio === "string" ? d.bio.replace(/https?:\/\/t\.co\/\S+/g, "").replace(/\s+\n/g, "\n").trim().slice(0, 500) || null : null;
  return { username, displayName, bio };
}

const PICTURE = /^data\/profile_media\/[^/]+\.(jpe?g|png)$/i;

/** Reads only account.js, profile.js and the profile picture out of the X archive. */
export async function xProfileFromZip(f: Blob): Promise<XProfile> {
  const entries = await zipEntries(f);
  const find = (re: RegExp) => entries.find((e) => re.test(e.name));
  const dec = new TextDecoder();
  const text = async (re: RegExp) => {
    const e = find(re);
    return e ? ytd(dec.decode(await readEntry(f, e, 1024 * 1024))) : [];
  };
  const fields = profileFrom(await text(/^(?:[^/]+\/)?data\/account\.js$/), await text(/^(?:[^/]+\/)?data\/profile\.js$/));
  const pic = entries.find((e) => PICTURE.test(e.name.replace(/^[^/]+\/(?=data\/)/, "")));
  let picture: Blob | null = null;
  if (pic) {
    try {
      picture = new Blob([(await readEntry(f, pic, 2 * 1024 * 1024)) as Uint8Array<ArrayBuffer>], { type: /\.png$/i.test(pic.name) ? "image/png" : "image/jpeg" });
    } catch {
      picture = null;
    }
  }
  return { ...fields, picture };
}

/** A square JPEG, 400 px across, from any image (the portrait accounts takes is 256 to 1024 px, ≤ 256 KB). */
export async function squarePortrait(img: Blob, side = 400): Promise<Blob> {
  const bmp = await createImageBitmap(img);
  const s = Math.min(bmp.width, bmp.height);
  const c = document.createElement("canvas");
  c.width = c.height = side;
  c.getContext("2d")!.drawImage(bmp, (bmp.width - s) / 2, (bmp.height - s) / 2, s, s, 0, 0, side, side);
  return new Promise((res, rej) => c.toBlob((b) => (b ? res(b) : rej(new Error("Couldn't make the picture."))), "image/jpeg", 0.86));
}
